import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/authStore';
import {
  getVisibleActions,
  getProminentActions,
  getInlineActions,
  getMenuActions,
  getActionsForTable,
  isActionVisible,
  type ActionConfig,
  type ActionUser,
  type TableName,
  type ActionContext,
} from '../config/actions';
import { api } from '../lib/api';

export interface UseActionsReturn {
  getActions: (context: ActionContext) => ActionConfig[];
  getProminent: () => ActionConfig[];
  getInline: () => ActionConfig[];
  getMenu: () => ActionConfig[];
  isVisible: (action: ActionConfig) => boolean;
  execute: (action: ActionConfig) => void;
  isExecuting: boolean;
  error: Error | null;
}

export interface UseActionsOptions {
  /** Called when EDIT_RECORD action is triggered */
  onEditRecord?: (row: Record<string, unknown>) => void;
  /** Called after DELETE_RECORD succeeds (e.g., to close a modal) */
  onAfterDelete?: () => void;
}

export function useActions(
  table: TableName,
  row: Record<string, unknown>,
  options?: UseActionsOptions
): UseActionsReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const actionUser: ActionUser = {
    email: user?.email || '',
    viewTags: user?.viewTags || [],
  };

  const executeMutation = useMutation({
    mutationFn: async (action: ActionConfig) => {
      switch (action.type) {
        case 'NAVIGATE_APP': {
          const config = action.config as {
            route: string | ((r: Record<string, unknown>) => string);
            params?: Record<string, string>;
          };
          // Support function-based routes for dynamic URLs with query strings
          let route = typeof config.route === 'function'
            ? config.route(row)
            : config.route;
          // Apply param replacements for static routes
          if (typeof config.route === 'string' && config.params) {
            for (const [param, field] of Object.entries(config.params)) {
              route = route.replace(`:${param}`, String(row[field] || ''));
            }
          }
          navigate(route);
          return;
        }

        case 'NAVIGATE_URL': {
          const config = action.config as { url: string | ((r: Record<string, unknown>) => string) };
          const url = typeof config.url === 'function' ? config.url(row) : config.url;
          window.open(url, '_blank');
          return;
        }

        case 'SET_COLUMN_VALUE': {
          const config = action.config as { column: string; value: unknown };
          const value = typeof config.value === 'function' ? config.value(row) : config.value;
          if (table === 'workorders' && row.id) {
            const response = await api.workorders.update(row.id as number, { [config.column]: value });
            if (!response.success) {
              throw new Error(response.error?.message || 'Failed to update work order');
            }
          }
          return;
        }

        case 'DELETE_RECORD': {
          if (!row.id) throw new Error('Cannot delete: missing row id');
          if (table === 'serviceLogs') {
            const response = await api.serviceLogs.delete(Number(row.id));
            if (!response.success) {
              throw new Error(response.error?.message || 'Failed to delete service log');
            }
            toast.success('Service log deleted');
            options?.onAfterDelete?.();
          } else if (table === 'workorders') {
            const response = await api.workorders.delete(Number(row.id));
            if (!response.success) {
              throw new Error(response.error?.message || 'Failed to delete work order');
            }
            toast.success('Work order archived');
            options?.onAfterDelete?.();
          } else {
            throw new Error(`DELETE_RECORD not implemented for table: ${table}`);
          }
          return;
        }

        case 'EMAIL': {
          const config = action.config as {
            to?: string | ((r: Record<string, unknown>) => string);
            subject?: string | ((r: Record<string, unknown>) => string);
            body?: string | ((r: Record<string, unknown>) => string);
          };
          const to = typeof config.to === 'function' ? config.to(row) : config.to || '';
          const subject = typeof config.subject === 'function' ? config.subject(row) : config.subject || '';
          const body = typeof config.body === 'function' ? config.body(row) : config.body || '';
          window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          return;
        }

        case 'CALL': {
          const config = action.config as { phoneColumn: string };
          const phone = row[config.phoneColumn] as string;
          if (phone && /^[+\d\s()\-.]+$/.test(phone)) {
            const sanitized = phone.replace(/[^\d+]/g, '');
            window.location.href = `tel:${sanitized}`;
          }
          return;
        }

        case 'SMS': {
          const config = action.config as { phoneColumn: string; body?: string | ((r: Record<string, unknown>) => string) };
          const phone = row[config.phoneColumn] as string;
          if (!phone || !/^[+\d\s()\-.]+$/.test(phone)) return;
          const sanitizedPhone = phone.replace(/[^\d+]/g, '');
          const body = typeof config.body === 'function' ? config.body(row) : config.body || '';
          window.location.href = `sms:${sanitizedPhone}${body ? `?body=${encodeURIComponent(body)}` : ''}`;
          return;
        }

        case 'EDIT_RECORD': {
          if (options?.onEditRecord) {
            options.onEditRecord(row);
          }
          return;
        }

        case 'COMPOSITE': {
          const compositeConfig = action.config as { actions: string[] };
          const allActions = getActionsForTable(table);

          for (const subActionId of compositeConfig.actions) {
            const subAction = allActions.find(a => a.id === subActionId);
            if (!subAction) {
              throw new Error(`Composite sub-action '${subActionId}' not found`);
            }
            if (subAction.type !== 'SET_COLUMN_VALUE') {
              throw new Error(`Composite sub-action '${subActionId}' must be SET_COLUMN_VALUE, got ${subAction.type}`);
            }
            const subConfig = subAction.config as { column: string; value: unknown };
            const value = typeof subConfig.value === 'function'
              ? subConfig.value(row)
              : subConfig.value;

            if (table !== 'workorders') {
              throw new Error(`COMPOSITE not implemented for table: ${table}`);
            }
            if (!row.id) {
              throw new Error('Cannot execute composite: missing row id');
            }
            const response = await api.workorders.update(row.id as number, { [subConfig.column]: value });
            if (!response.success) {
              throw new Error(response.error?.message || `Failed: ${subActionId}`);
            }
          }
          return;
        }

        case 'ADD_RECORD':
        case 'ADD_RECORD_TO':
        case 'EXPORT_VIEW':
          // Not yet implemented
          return;

        default:
          console.warn('Unknown action type:', action.type);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [table] });
      if (table === 'workorders') {
        queryClient.invalidateQueries({ queryKey: ['workorders-stats'] });
      }
    },
    onError: (error: Error) => {
      toast.error(`Action failed: ${error.message}`);
    },
  });

  return {
    getActions: (context) => getVisibleActions(table, row, actionUser, context),
    getProminent: () => getProminentActions(table, row, actionUser),
    getInline: () => getInlineActions(table, row, actionUser),
    getMenu: () => getMenuActions(table, row, actionUser),
    isVisible: (action) => isActionVisible(action, row, actionUser),
    execute: (action) => executeMutation.mutate(action),
    isExecuting: executeMutation.isPending,
    error: executeMutation.error,
  };
}
